<template>
    <div class="payment">
        <div class="payment__header">
            <h2 class="payment__title">
                {{ $t('pages.payments.paymentProcessing') }}

                <template v-if="clientFullNameWithPhone">
                    {{ clientFullNameWithPhone }}
                </template>

                <v-progress-circular
                    v-if="isLoading"
                    indeterminate
                    color="grey"
                    size="24"
                    class="payment__loading"
                ></v-progress-circular>
            </h2>
            <button class="payment__back-btn" @click="goBack">
                <v-icon class="payment__back-icon">mdi-chevron-left</v-icon>
                {{ $t('pages.payments.returnToWig') }}
            </button>
        </div>
        <v-form
            v-model="formValid"
            @submit.prevent="onConfirm"
            class="payment__block"
        >
            <div class="payment__client">
                <v-text-field
                    v-model="clientsName"
                    :rules="[valid.clientsName]"
                    light
                    outlined
                    dense
                    type="text"
                    name="clientsName"
                    :label="$t('pages.payments.clientsName')"
                    class="payment-field"
                    @input="updateClientInfo"
                ></v-text-field>

                <v-text-field
                    v-model="optionalId"
                    :rules="[valid.optionalId]"
                    light
                    outlined
                    dense
                    type="number"
                    name="optionalId"
                    :label="$t('pages.payments.optionalID')"
                    class="payment-field"
                    @input="updateClientInfo"
                ></v-text-field>

                <v-checkbox
                    v-model="includesVAT"
                    :label="$t('pages.payments.includeVAT')"
                    dense
                    class="payment-field"
                    @change="handleVATChange"
                ></v-checkbox>

                <v-progress-circular
                    v-if="isLoading"
                    indeterminate
                    color="grey"
                    size="24"
                    class="payment__loading"
                ></v-progress-circular>
            </div>

            <div class="payment__list">
                <div
                    v-for="(payment, index) in processedPayments"
                    :key="index"
                    class="payment__list-item"
                >
                    <div
                        class="payment__entry"
                        :style="{ backgroundColor: payment.color }"
                    >
                        <v-btn
                            color="#B05454"
                            class="payment__remove"
                            @click="removePayment(payment)"
                        >
                            <v-icon class="payment__remove-icon" small>
                                mdi mdi-close-circle-outline
                            </v-icon>
                        </v-btn>

                        <div>
                            <v-select
                                v-if="!isCard(payment.payment_type) && !isBankDebit(payment.payment_type)"
                                v-model="payment.payment_type"
                                :items="paymentTypes"
                                :item-text="getTranslationForPaymentTypes"
                                item-value="payment_type"
                                :label="$t('pages.payments.selectPaymentType')"
                                outlined
                                dense
                                class="payment-field"
                                @change="
                                    handlePaymentTypeChange(
                                        payment.local_id,
                                        payment.payment_type
                                    )
                                "
                            ></v-select>
                            <div v-else class="payment-field__block">
                                <small class="small">{{
                                    $t('pages.payments.paymentType')
                                }}</small>
                                {{ getPaymentTypeByType(payment.payment_type) }}
                            </div>
                        </div>

                        <component
                            :is="getPaymentFormComponent(payment.payment_type)"
                            v-bind="payment"
                            :hide-fields="true"
                            @update-payment="updatePayment"
                        ></component>
                    </div>

                    <div
                        v-if="isLastOfType(processedPayments, payment)"
                        class="payment__comment"
                        :style="{ backgroundColor: payment.color }"
                    >
                        <v-textarea
                            v-model="comments[payment.payment_type]"
                            light
                            outlined
                            dense
                            no-resize
                            type="text"
                            name="commentForPayment"
                            :label="$t('pages.payments.commentForPaymentType')"
                            class="payment-field payment__comment-field payment__textarea"
                            @change="handleComment"
                        ></v-textarea>
                    </div>
                </div>
            </div>

            <div class="payment__additional">
                <div v-if="totalAmount > 0" class="payment__total">
                    <div class="payment__total-title">
                        {{ $t('pages.payments.totalAmount') }}
                    </div>
                    {{ totalAmount }}
                </div>

                <v-textarea
                    v-model="description"
                    :rules="[valid.description]"
                    light
                    outlined
                    dense
                    type="text"
                    name="description"
                    no-resize
                    :label="$t('pages.payments.enterDescription')"
                    class="payment__textarea"
                    @input="updateDescription"
                ></v-textarea>

                <v-radio-group
                    v-model="invoiceAndReceipt"
                    class="payment-field payment__radio"
                    @change="handleReceiptChange"
                >
                    <v-radio
                        :label="$t('pages.payments.receiptOnly')"
                        small
                        :value="false"
                    ></v-radio>
                    <v-radio
                        :label="$t('pages.payments.taxInvoiceAndReceipt')"
                        small
                        :value="true"
                    ></v-radio>
                </v-radio-group>
            </div>

            <div class="payment__actions">
                <v-btn
                    color="#37BC9B"
                    dark
                    class="payment__button"
                    @click="addPayment"
                    >{{ $t('pages.payments.addPayment') }}</v-btn
                >

                <v-tooltip top :disabled="isAbleToSend">
                    <template v-slot:activator="{ on, attrs }">
                        <div
                            v-bind="attrs"
                            v-on="on"
                            style="display: inline-block"
                        >
                            <v-btn
                                :disabled="!isAbleToSend"
                                :dark="isAbleToSend"
                                color="#967adc"
                                class="payment__button"
                                type="submit"
                            >
                                {{ $t('pages.payments.confirm') }}
                            </v-btn>
                        </div>
                    </template>
                    <span>{{ $t('pages.payments.checkFields') }}</span>
                </v-tooltip>
            </div>
        </v-form>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import PaymentCashForm from '@/components/payments/payment-cash-form.vue'
import PaymentCheckForm from '@/components/payments/payment-check-form.vue'
import PaymentCardForm from '@/components/payments/payment-card-form.vue'
import PaymentBankDebitForm from '@/components/payments/payment-bank-debit-form.vue'
import PaymentTransferForm from '@/components/payments/payment-transfer-form.vue'

import {
    paymentsV2ActionTypes,
    paymentsV2GetterTypes,
} from '@/store/modules/payments_v2/types'
import { actionTypes as actionTypesModals } from '@/store/modules/modals'
import popUpTypes from '@/types/pop-up-types'
import { actionTypes as actionTypesConfirmer } from '@/store/modules/confirmer'
import { actionTypes as actionTypesModalNotifications } from '@/store/modules/modal-notifications'
const { paymentAdd, confirmPayment } = popUpTypes

export default {
    components: {
        PaymentCashForm,
        PaymentCheckForm,
        PaymentCardForm,
        PaymentBankDebitForm,
        PaymentTransferForm,
    },
    data() {
        return {
            selectedPaymentType: null,
            paymentData: {},
            total: 0,
            description: '',
            clientsName: '',
            includesVAT: true,
            optionalId: null,
            comments: {},
            invoiceAndReceipt: true,
            formValid: false,
            isSubmitting: false,
            valid: {
                clientsName: (v) =>
                    !!v || this.$t('pages.payments.fieldIsRequired'),
                description: (v) =>
                    !!v || this.$t('pages.payments.fieldIsRequired'),
                optionalId: (v) => !v || v.length === 9 || 'Must be 9 digits',
            },
        }
    },
    computed: {
        ...mapGetters('paymentsV2', {
            paymentTypes: paymentsV2GetterTypes.GET_PAYMENT_TYPES,
            payments: paymentsV2GetterTypes.GET_PAYMENTS,
            client: paymentsV2GetterTypes.GET_CLIENT,
            isLoading: paymentsV2GetterTypes.GET_LOADING,
        }),
        isAbleToSend() {
            return this.payments?.length > 0 && this.formValid
        },
        totalAmount: function () {
            const total = this.payments.reduce(
                (total, payment) => total + (+payment.amount || 0),
                0
            )

            return parseFloat(total.toFixed(2))
        },
        wigID() {
            return this.$route.params.wigID
        },
        clientFullNameWithPhone: function () {
            if (Object.keys(this.client).length === 0) return ''

            return `( ${this.client.phone} ) ${this.client.clientsName}`
        },
        processedPayments: function () {
            let currentColor = '#F9F9FF'
            let lastType = null

            return this.payments.map((payment) => {
                if (payment.payment_type !== lastType) {
                    currentColor =
                        currentColor === '#F9F9FF' ? '#FFF9F9' : '#F9F9FF'
                    lastType = payment.payment_type
                }

                return { ...payment, color: currentColor }
            })
        },
    },
    watch: {
        client: function () {
            if (!this.client.clientsName) return

            this.clientsName = this.client.clientsName
        },
    },
    created() {
        this.resetState()
        this.fetchPaymentTypes()
        this.fetchBankCodes()
        this.fetchClientByWigId(this.wigID)
    },
    methods: {
        ...mapActions('paymentsV2', {
            fetchPaymentTypes: paymentsV2ActionTypes.FETCH_PAYMENT_TYPES,
            fetchBankCodes: paymentsV2ActionTypes.FETCH_BANK_CODES,
            removePayment: paymentsV2ActionTypes.REMOVE_PAYMENT,
            updatePaymentData: paymentsV2ActionTypes.UPDATE_PAYMENT,
            setPaymentDescription:
                paymentsV2ActionTypes.SET_PAYMENT_DESCRIPTION,
            fetchClientByWigId: paymentsV2ActionTypes.FETCH_CLIENT_BY_WIG_ID,
            setClientInfo: paymentsV2ActionTypes.SET_CLIENT,
            setIncludesVat: paymentsV2ActionTypes.SET_VAT,
            setComments: paymentsV2ActionTypes.SET_COMMENTS,
            setInvoiceAndReceipt: paymentsV2ActionTypes.SET_INVOICE_AND_RECEIPT,
            resetState: paymentsV2ActionTypes.RESET_STATE,
        }),
        ...mapActions('modals', {
            onOpen: actionTypesModals.openPopUp,
        }),
        ...mapActions('confirmer', {
            confirmHandle: actionTypesConfirmer.confirmHandle,
        }),
        ...mapActions('modalNotifications', {
            success: actionTypesModalNotifications.success,
            error: actionTypesModalNotifications.error,
        }),
        openConfirmPopUp: async function (message, confirmText, cancelText) {
            try {
                await this.confirmHandle({
                    text: message,
                    yesText: confirmText || '',
                    noText: cancelText || '',
                    cancelColor: cancelText ? '#967adc' : '',
                })
                return true
            } catch (e) {
                return false
            }
        },
        getPaymentFormComponent: function (type_id) {
            if (type_id === 1) {
                return 'PaymentCheckForm'
            }

            if (type_id === 2) {
                return 'PaymentCashForm'
            }

            if (type_id === 9) {
                return 'PaymentTransferForm'
            }

            if (type_id === 100) {
                return 'PaymentBankDebitForm'
            }

            if (this.isCard(type_id)) {
                return 'PaymentCardForm'
            }
        },
        addPayment: function () {
            this.onOpen(paymentAdd)
        },
        onConfirm: async function () {
            if (this.optionalId) {
                await this.handleConfirm()
                return
            }

            const res = await this.openConfirmPopUp(
                this.$t('pages.payments.confirmWithoutOptionalID')
            )

            if (!res) return

            await this.handleConfirm()
        },
        handleConfirm: async function () {
            const payload = false
            this.onOpen({ name: confirmPayment, payload })
        },
        resetForm: function () {
            this.selectedPaymentType = null
            this.paymentData = {}
        },
        openAdd: function (isRepair) {
            const payload = { isRepair }
            this.onOpen({ name: paymentAdd, payload })
        },
        handlePaymentTypeChange: function (local_id, payment_type) {
            this.updatePayment({ local_id, payment_type })
        },
        updatePayment: function (updatedData) {
            this.updatePaymentData(updatedData)
        },
        goBack: function () {
            const tabHasHistory = window.history.length > 1

            if (tabHasHistory) {
                return this.$router.back()
            }

            return this.$router.push('/orders')
        },
        isCard: function (payment_type) {
            return [4, 5, 6, 7, 8, 10, 11].includes(payment_type)
        },
        isBankDebit: function (payment_type) {
            return payment_type === 100
        },
        updateDescription: function () {
            this.setPaymentDescription(this.description)
        },
        updateClientInfo: function () {
            this.setClientInfo({
                name: this.clientsName,
                optionalId: this.optionalId,
            })
        },
        handleVATChange: function () {
            this.setIncludesVat(this.includesVAT)
        },
        handleComment: function () {
            this.setComments(this.comments)
        },
        handleReceiptChange: function () {
            this.setInvoiceAndReceipt(this.invoiceAndReceipt)
        },
        isLastOfType: function (payments, targetPayment) {
            const lastIndex = [...payments]
                .reverse()
                .findIndex(
                    (payment) =>
                        payment.payment_type === targetPayment.payment_type
                )

            if (lastIndex === -1) return false

            const actualIndex = payments.length - 1 - lastIndex
            return actualIndex === payments.indexOf(targetPayment)
        },
        getTranslationForPaymentTypes: function (item) {
            const translatedKey = `pages.payments.${item.payment_name}`
            const translatedText = this.$t(translatedKey)

            if (translatedText.includes('Translation not found for key')) {
                return item.payment_name
            }

            return translatedText
        },
        getPaymentTypeByType: function (type) {
            if (this.paymentTypes.length < 1 || !type) return

            const filteredPayment = this.paymentTypes.filter(
                (payment) => payment.payment_type === type
            )

            return this.getTranslationForPaymentTypes(filteredPayment[0])
        },
    },
}
</script>

<style lang="scss">
.payment {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 16px 32px;

    &__header {
        margin-bottom: 48px;
        display: flex;
        flex-direction: column;
        justify-content: flex-start;
        width: 100%;
    }

    &__title {
        margin-bottom: 8px;
    }

    &__back-btn {
        width: fit-content;
    }

    &__back-icon {
        @include for-hebrew {
            transform: scale(-1);
        }
    }

    &__actions {
        display: flex;
        gap: 16px;
        justify-content: space-between;
    }

    &__button {
        width: 175px;
    }

    &__additional {
        padding-top: 24px;
        width: 100%;
        display: flex;
        gap: 32px;
        padding-left: 80px;

        @include for-hebrew {
            padding-left: 0;
            padding-right: 80px;
        }
    }

    &__block {
        width: 100%;
        padding: 32px 0;
        box-shadow: 0 10px 10px rgba(0, 0, 0, 0.1);

        border: 1px #967adc solid;
        border-radius: 16px;

        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: center;
    }

    &__list {
        width: 100%;
    }

    &__list-item {
        width: 100%;
        border-top: 1px solid #c3c3c3;

        &:last-child {
            border-bottom: 1px solid #c3c3c3;
        }
    }

    &__remove {
        color: #000 !important;
        background: transparent !important;
        box-shadow: none !important;
        aspect-ratio: 1 !important;
        min-width: 32px !important;
        height: 32px !important;
        margin-top: 4px;
        border-radius: 100% !important;
    }

    &__remove-icon {
        font-size: 24px !important;
    }

    &__entry {
        width: 100%;
        display: flex;
        gap: 16px;
        padding: 28px 34px 0 34px;
        background: #fff9f9;
    }

    &__comment {
        @extend .payment__entry;

        border-top: 1px solid #c3c3c3;
        padding-left: 80px;

        @include for-hebrew {
            padding-left: 0;
            padding-right: 80px;
        }
    }

    .payment-fields {
        width: 100%;
        transition: all 0.3s ease-in-out;
        display: flex;
        justify-content: flex-start;
        align-items: center;
        gap: 16px;
    }

    .payment-field {
        width: 100%;
        max-width: 232px !important;
        min-width: 150px;
    }

    &__textarea {
        max-width: 312px !important;
    }

    &__textarea .v-input__slot {
        height: 80px !important;
        width: 312px !important;
    }

    &__client {
        width: 100%;
        padding-left: 80px;
        display: flex;
        gap: 16px;
        justify-content: flex-start;
        align-items: flex-start;

        @include for-hebrew {
            padding-left: 0;
            padding-right: 80px;
        }
    }

    &__total {
        display: flex;
        flex-direction: column;
        font-size: 24px;
        color: #000;
    }

    &__total-title {
        color: #ababab;
        font-size: 18px;
    }

    &__radio {
        margin-top: 0 !important;
    }

    .v-progress-circular {
        margin-top: 8px;
    }

    .payment-field__block {
        display: flex;
        flex-direction: column;
        margin-bottom: 28px;
    }

    .small {
        font-size: 12px;
        color: #ababab;
        text-wrap: nowrap;
    }
}
</style>
